import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Boxes, Plus, Trash2, Save, Loader2, Palette, Tag } from "lucide-react";

export default function ContainerListsPage() {
  const [types, setTypes] = useState([]);
  const [colors, setColors] = useState([]);
  const [prefixes, setPrefixes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/container-config"); setTypes(data.types || []); setColors(data.colors || []); setPrefixes(data.prefixes || []); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (types.some((t) => !t.label.trim())) { toast.error("Every type needs a name."); return; }
    if (colors.some((c) => !c.label.trim())) { toast.error("Every color needs a name."); return; }
    if (prefixes.some((p) => !p.label.trim())) { toast.error("Every prefix needs a value."); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/container-config", { types, colors, prefixes });
      setTypes(data.types); setColors(data.colors); setPrefixes(data.prefixes || []);
      toast.success("Container lists saved.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save."); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 font-mono text-xs text-slate-400">LOADING…</div>;

  return (
    <div data-testid="container-lists-page" className="max-w-3xl">
      <h1 className="font-display text-2xl font-bold tracking-tight mb-1 flex items-center gap-2">
        <Boxes className="w-6 h-6 text-[#B01E28]" />Container types &amp; colors
      </h1>
      <p className="text-sm text-slate-500 mb-6">Manage the container type list, colors, and each type's default label size. Renames apply everywhere instantly; a type/color still in use can't be removed (reassign those containers first).</p>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg font-bold text-slate-800">Types</h2>
          <Button data-testid="add-type" size="sm" variant="outline" className="rounded-lg border-slate-200" onClick={() => setTypes([...types, { key: "", label: "", default_size: "4x3" }])}>
            <Plus className="w-4 h-4 mr-1" />Add type
          </Button>
        </div>
        <div className="space-y-2">
          {types.map((t, i) => (
            <div key={i} data-testid={`type-row-${i}`} className="flex items-center gap-2">
              <Input data-testid={`type-name-${i}`} value={t.label} placeholder="Type name (e.g. Bin)"
                onChange={(e) => setTypes(types.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="rounded-lg border-slate-200 flex-1" />
              <Select value={t.default_size} onValueChange={(v) => setTypes(types.map((x, j) => j === i ? { ...x, default_size: v } : x))}>
                <SelectTrigger data-testid={`type-size-${i}`} className="rounded-lg border-slate-200 w-36"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="4x3">4×3 sticker</SelectItem>
                  <SelectItem value="4x6">4×6 hang tag</SelectItem>
                </SelectContent>
              </Select>
              <Button data-testid={`type-del-${i}`} size="icon" variant="ghost" className="text-[#B01E28] shrink-0" onClick={() => setTypes(types.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg font-bold text-slate-800 flex items-center gap-2"><Palette className="w-5 h-5 text-[#C76714]" />Colors</h2>
          <Button data-testid="add-color" size="sm" variant="outline" className="rounded-lg border-slate-200" onClick={() => setColors([...colors, { key: "", label: "", hex: "#6B7280" }])}>
            <Plus className="w-4 h-4 mr-1" />Add color
          </Button>
        </div>
        <div className="space-y-2">
          {colors.map((c, i) => (
            <div key={i} data-testid={`color-row-${i}`} className="flex items-center gap-2">
              <input type="color" data-testid={`color-hex-${i}`} value={c.hex} onChange={(e) => setColors(colors.map((x, j) => j === i ? { ...x, hex: e.target.value } : x))} className="w-10 h-9 rounded-lg border border-slate-200 cursor-pointer shrink-0" />
              <Input data-testid={`color-name-${i}`} value={c.label} placeholder="Color name (e.g. Blue)"
                onChange={(e) => setColors(colors.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="rounded-lg border-slate-200 flex-1" />
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 w-24"><span className="w-4 h-4 rounded-full border border-slate-300" style={{ backgroundColor: c.hex }} />{c.hex}</span>
              <Button data-testid={`color-del-${i}`} size="icon" variant="ghost" className="text-[#B01E28] shrink-0" onClick={() => setColors(colors.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg font-bold text-slate-800 flex items-center gap-2"><Tag className="w-5 h-5 text-[#B01E28]" />Code prefixes</h2>
          <Button data-testid="add-prefix" size="sm" variant="outline" className="rounded-lg border-slate-200" onClick={() => setPrefixes([...prefixes, { key: "", label: "" }])}>
            <Plus className="w-4 h-4 mr-1" />Add prefix
          </Button>
        </div>
        <p className="text-xs text-slate-500 mb-3">The code stem for new vessels — <span className="font-mono">TOTE-001</span>, <span className="font-mono">BIN-001</span>. Uppercased, letters/numbers only. Changes affect only newly generated codes; existing containers keep their code.</p>
        <div className="space-y-2">
          {prefixes.map((p, i) => (
            <div key={i} data-testid={`prefix-row-${i}`} className="flex items-center gap-2">
              <Input data-testid={`prefix-name-${i}`} value={p.label} placeholder="Prefix (e.g. TOTE)"
                onChange={(e) => setPrefixes(prefixes.map((x, j) => j === i ? { ...x, label: e.target.value.toUpperCase() } : x))} className="rounded-lg border-slate-200 flex-1 font-mono" />
              <Button data-testid={`prefix-del-${i}`} size="icon" variant="ghost" className="text-[#B01E28] shrink-0" onClick={() => setPrefixes(prefixes.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          {prefixes.length === 0 && <p className="text-xs font-mono text-slate-400">No prefixes — add at least one.</p>}
        </div>
      </div>

      <Button data-testid="save-container-lists" disabled={saving} onClick={save} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}Save lists
      </Button>
    </div>
  );
}
